/**
 * CODEMIND AI - MAIN APPLICATION COMPONENT
 * Revolutionary AI-Powered Development Assistant
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { ChatInterface } from './components/ChatInterface';
import { CodeEditor } from './components/CodeEditor';
import { VoiceInterface } from './components/VoiceInterface';
import { Dashboard } from './components/Dashboard';
import { ProjectManager } from './components/ProjectManager';
import { SettingsPanel } from './components/SettingsPanel';
import { LoadingScreen } from './components/LoadingScreen';

// Hooks and Services
import { useWebSocket } from './hooks/useWebSocket';
import { useAIService } from './hooks/useAIService';
import { useSessionStore } from './stores/sessionStore';
import { useThemeStore } from './stores/themeStore';

// Styles
import './styles/globals.css';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState('chat');
  
  // Store hooks
  const { sessionId, initializeSession } = useSessionStore();
  const { theme } = useThemeStore();
  
  // Service hooks
  const { isConnected, sendMessage } = useWebSocket(sessionId);
  const { isAIReady } = useAIService();

  useEffect(() => {
    // Initialize application
    const initApp = async () => {
      try {
        await initializeSession();
        
        // Simulate loading time for smooth UX
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
        
      } catch (error) {
        console.error('App initialization error:', error);
        setIsLoading(false);
      }
    };

    initApp();
  }, [initializeSession]);

  // Apply theme
  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className={`app ${theme} min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900`}>
          {/* Background Effects */}
          <div className="fixed inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
          <div className="fixed inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-teal-600/20 animate-pulse" />
          
          {/* Main Layout */}
          <div className="relative z-10 flex h-screen">
            {/* Sidebar */}
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="w-80 border-r border-white/10 backdrop-blur-xl bg-white/5"
                >
                  <Sidebar 
                    activeView={activeView}
                    onViewChange={(view) => setActiveView(view)}
                    isConnected={isConnected}
                    isAIReady={isAIReady}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
              {/* Header */}
              <Header 
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                activeView={activeView}
                sessionId={sessionId}
                isConnected={isConnected}
              />

              {/* Content Area */}
              <main className="flex-1 overflow-hidden">
                <Routes>
                  <Route path="/" element={
                    <MainContent 
                      activeView={activeView}
                      sessionId={sessionId}
                      sendMessage={sendMessage}
                    />
                  } />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/projects" element={<ProjectManager />} />
                  <Route path="/settings" element={<SettingsPanel />} />
                </Routes>
              </main>
            </div>
          </div>

          {/* Voice Interface Overlay */}
          <VoiceInterface sessionId={sessionId} />
          
          {/* Global Notifications */}
          <div id="toast-root" />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

// Main Content Component
interface MainContentProps {
  activeView: string;
  sessionId: string;
  sendMessage: (message: any) => void;
}

function MainContent({ activeView, sessionId, sendMessage }: MainContentProps) {
  return (
    <div className="h-full flex">
      <AnimatePresence mode="wait">
        {activeView === 'chat' && (
          <motion.div
            key="chat"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            <ChatInterface 
              sessionId={sessionId}
              sendMessage={sendMessage}
            />
          </motion.div>
        )}

        {activeView === 'code' && (
          <motion.div
            key="code"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            <CodeEditor 
              sessionId={sessionId}
              sendMessage={sendMessage}
            />
          </motion.div>
        )}

        {activeView === 'dashboard' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            <Dashboard />
          </motion.div>
        )}

        {activeView === 'projects' && (
          <motion.div
            key="projects"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            <ProjectManager />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;